import { useEffect, useState } from "react";
import { ExternalLink, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Textarea } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import type { IntakeForm } from "@/shared/types";

export function FormsAdminPage() {
  const [forms, setForms] = useState<IntakeForm[]>([]);
  const [creating, setCreating] = useState(false);
  const load = () => api<IntakeForm[]>("/admin/forms").then(setForms);
  useEffect(() => {
    void load();
  }, []);
  const create = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    await api("/admin/forms", {
      method: "POST",
      body: JSON.stringify({
        slug: data.get("slug"),
        location_id: "mountain-view",
        title: { "en-US": data.get("title_en"), "es-US": data.get("title_es") },
        description: { "en-US": data.get("description_en") },
        acknowledgement: {
          "en-US": "Thank you. Your information was received.",
          "es-US": "Gracias. Recibimos tu información.",
        },
        fields: [],
        active: false,
      }),
    });
    setCreating(false);
    await load();
  };
  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Forms</h1>
          <p className="mt-2 text-muted-foreground">
            Localized neighbor-facing forms that feed profiles automatically.
          </p>
        </div>
        <Button onClick={() => setCreating(!creating)}>
          <Plus size={17} />
          New form
        </Button>
      </div>
      {creating && (
        <Card className="mt-6">
          <CardContent className="p-6">
            <form onSubmit={create} className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-2 block">English title</Label>
                <Input name="title_en" required />
              </div>
              <div>
                <Label className="mb-2 block">Spanish title</Label>
                <Input name="title_es" />
              </div>
              <div>
                <Label className="mb-2 block">URL slug</Label>
                <Input name="slug" required />
              </div>
              <div>
                <Label className="mb-2 block">English description</Label>
                <Textarea name="description_en" />
              </div>
              <div className="md:col-span-2">
                <Button type="submit">Create draft</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      <div className="mt-7 grid gap-5 md:grid-cols-2">
        {forms.map((form) => (
          <Card key={form.id}>
            <CardHeader>
              <div className="flex justify-between">
                <Badge>{form.active ? "Published" : "Draft"}</Badge>
                <span className="text-xs text-muted-foreground">
                  {form.fields.length} fields
                </span>
              </div>
              <CardTitle className="pt-3">{form.title["en-US"]}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">/{form.slug}</p>
              {form.active && (
                <a
                  href={`/form/${form.slug}`}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary"
                >
                  Open form <ExternalLink size={14} />
                </a>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="mt-6 border-amber-200 bg-amber-50">
        <CardContent className="p-5 text-sm text-amber-900">
          The neighbor intake field set is implemented. A general-purpose
          drag-and-drop field editor should be shaped by feedback on the first
          real forms.
        </CardContent>
      </Card>
    </div>
  );
}
